import type { DealStage } from "../../lib/types";

/**
 * Stage Transition Button Configuration
 *
 * Maps each stage to:
 *   - buttonLabel: what the transition button says (action-oriented)
 *   - fields: what the inline form collects (each field saves directly to the deal)
 *   - nextStage: where the deal goes when all items are met
 *
 * Fields with type "check" are auto-evaluated from deal data.
 * Fields with type "input" show an editable form field.
 */

export type FieldType = "text" | "date" | "datetime" | "select" | "phone" | "number" | "currency" | "upload" | "check";

export interface TransitionField {
  key: string;
  label: string;
  /** The deal column this field reads/writes */
  dealColumn: string;
  type: FieldType;
  required: boolean;
  /** For selects */
  options?: Array<{ value: string; label: string }>;
  /** For checks — auto-evaluated, not user-editable */
  checkFn?: string;
  /** Placeholder text */
  placeholder?: string;
  /**
   * For uploads — the deal_documents.file_type the picked file is stored under.
   * Jul 15, 2026: TransitionButton previously discarded the picked file and only
   * flipped the boolean dealColumn (the "revised estimate never attached to the
   * invoice email" bug). Every upload field must carry a docType so the file
   * actually lands in storage + deal_documents.
   */
  docType?: string;
  /** Small helper text rendered under the field label. */
  hint?: string;
  /**
   * Sep 15, 2026 — soft sanity check rendered under the input (never blocks).
   * "supplement_vs_rcv": warn when a supplement request is ≥ 70% of the
   * carrier's RCV, which almost always means Apex's whole rebuilt estimate
   * total was typed instead of the added amount (23 deals were found that way).
   */
  warnFn?: "supplement_vs_rcv";
}

export interface TransitionConfig {
  buttonLabel: string;
  subtitle?: string;
  nextStage: DealStage;
  nextPhase?: string;
  fields: TransitionField[];
  /**
   * Columns automatically stamped on the deal when the transition advances.
   * Value "today" => YYYY-MM-DD (for date columns).
   * Value "now"   => current ISO timestamp (for timestamptz columns).
   * Used to record claim_called_in_date when a deal moves leads → claims,
   * separately from any date the user types into the form (which is the
   * storm / loss-event date and lives in date_of_loss).
   */
  autoStamps?: Record<string, "today" | "now">;
}

// Apr 30, 2026: SHINGLE_OPTIONS and DRIP_OPTIONS removed from this file.
// They were only consumed by the install_meeting transition fields (now empty)
// and the homeowner color-pick UI sources its own list directly. If we ever
// add a staff-side color admin dropdown, restore the lists.

const PRESENTATION_TYPE_OPTIONS = [
  { value: "live", label: "Live (in person)" },
  { value: "call", label: "Call (phone/video)" },
];

/**
 * Stage → transition config.
 * Key = current stage, value = what happens when RM clicks the button.
 */
export const TRANSITION_CONFIGS: Partial<Record<DealStage, TransitionConfig>> = {
  // ── LEADS ──
  initial_contact: {
    buttonLabel: "Schedule presentation",
    subtitle: "Set up the homeowner presentation",
    nextStage: "presentation_scheduled",
    fields: [
      { key: "pres_date", label: "Presentation date & time", dealColumn: "presentation_date", type: "datetime", required: true, placeholder: "Select date and time" },
      { key: "pres_type", label: "Presentation type", dealColumn: "presentation_type", type: "select", required: true, options: PRESENTATION_TYPE_OPTIONS },
    ],
  },
  presentation_scheduled: {
    buttonLabel: "Complete presentation",
    subtitle: "Mark the presentation as done",
    nextStage: "follow_up",
    fields: [],
  },
  follow_up: {
    buttonLabel: "File insurance claim",
    subtitle: "Convert this lead to a claim",
    nextStage: "assign_roof_specialist",
    nextPhase: "claims",
    fields: [
      { key: "insurance", label: "Insurance company", dealColumn: "insurance_company", type: "text", required: true, placeholder: "e.g. State Farm" },
      { key: "claim_num", label: "Claim number", dealColumn: "claim_number", type: "text", required: true, placeholder: "e.g. CLM-12345" },
      { key: "dol", label: "Date of loss / discovery", dealColumn: "date_of_loss", type: "date", required: true },
    ],
    autoStamps: { claim_called_in_date: "today" },
  },
  waiting: {
    buttonLabel: "File insurance claim",
    subtitle: "Convert this lead to a claim",
    nextStage: "assign_roof_specialist",
    nextPhase: "claims",
    fields: [
      { key: "insurance", label: "Insurance company", dealColumn: "insurance_company", type: "text", required: true, placeholder: "e.g. State Farm" },
      { key: "claim_num", label: "Claim number", dealColumn: "claim_number", type: "text", required: true, placeholder: "e.g. CLM-12345" },
      { key: "dol", label: "Date of loss / discovery", dealColumn: "date_of_loss", type: "date", required: true },
    ],
    autoStamps: { claim_called_in_date: "today" },
  },

  // ── CLAIMS ──
  assign_roof_specialist: {
    buttonLabel: "Complete pre-inspection",
    subtitle: "RS assigned → pre-inspection done. RM schedules adjuster meeting in the next step.",
    nextStage: "pre_inspection_complete",
    fields: [
      { key: "rs", label: "Roof specialist assigned", dealColumn: "assigned_rs_id", type: "select", required: true, options: [] },
      { key: "pre_findings", label: "Pre-inspection notes (optional)", dealColumn: "pre_inspection_findings", type: "text", required: false, placeholder: "Damage observed, slopes affected, photos uploaded to CompanyCam, anything the RM needs to know..." },
    ],
  },
  pre_inspection_complete: {
    buttonLabel: "Schedule adjuster meeting",
    subtitle: "Once you have a date from the adjuster, log it here to advance.",
    nextStage: "adjuster_meeting_ready",
    fields: [
      { key: "adj_date", label: "Adjuster meeting start (date & time)", dealColumn: "adjuster_meeting_date", type: "datetime", required: true },
      { key: "adj_name", label: "Adjuster name", dealColumn: "adjuster_name", type: "text", required: false, placeholder: "e.g. Mike Johnson" },
      { key: "adj_phone", label: "Adjuster phone", dealColumn: "adjuster_phone", type: "phone", required: false, placeholder: "(770) 555-0000" },
    ],
  },
  adjuster_meeting_ready: {
    buttonLabel: "Complete adjuster meeting",
    subtitle: "Record outcome and wait for insurance decision",
    nextStage: "waiting_on_claims",
    fields: [
      { key: "outcome", label: "Adjuster meeting notes", dealColumn: "adjuster_meeting_outcome", type: "text", required: true, placeholder: "What happened at the meeting?" },
    ],
  },
  waiting_on_claims: {
    buttonLabel: "Insurance approved",
    subtitle: "Upload insurance paperwork and move to install meeting",
    nextStage: "install_meeting",
    nextPhase: "builds",
    fields: [
      { key: "paperwork", label: "Insurance paperwork uploaded", dealColumn: "insurance_paperwork_uploaded", type: "upload", required: true, docType: "insurance_scope" },
    ],
  },

  // ── REINSPECTIONS ──
  docs_and_contingency: {
    buttonLabel: "Prepare reinspection",
    subtitle: "Gather all documents for reinspection submission",
    nextStage: "reinspection_ready",
    fields: [
      { key: "contingency", label: "Signed contingency from homeowner", dealColumn: "contingency_signed", type: "check", required: true, checkFn: "contingency_signed" },
      { key: "insurance_docs", label: "Insurance paperwork uploaded", dealColumn: "insurance_paperwork_uploaded", type: "upload", required: true, docType: "insurance_scope" },
      { key: "hover", label: "Hover measurements ordered", dealColumn: "hover_ordered", type: "check", required: true, checkFn: "hover_ordered" },
      { key: "apex", label: "Apex estimate generated", dealColumn: "apex_estimate_generated", type: "check", required: true, checkFn: "apex_estimate_generated" },
    ],
  },
  reinspection_ready: {
    buttonLabel: "Submit reinspection",
    subtitle: "Send reinspection email to insurance",
    nextStage: "reinspection_filed",
    fields: [
      { key: "email1", label: "Reinspection Email 1 sent", dealColumn: "reinspection_email1_sent_at", type: "check", required: true, checkFn: "reinspection_email1_sent_at" },
    ],
  },

  // ── BUILDS ──
  // Apr 30, 2026: install_meeting fields stripped. ACV / deductible are entered
  // through the Scope editor now (single source of truth). Preferred date is
  // collected post-signing via the ScopeSection inline form (which gates the
  // schedule_build release-to-Robert flow, not build_submitted). Shingle / drip
  // colors are chosen by the homeowner during signing. The transition is now a
  // confirm click — the gates in stageRequirements.ts (insurance paperwork
  // uploaded + scope entered) prove everything is in place.
  install_meeting: {
    buttonLabel: "Submit for build approval",
    subtitle: "Sends it to the Build Approvals board, where Chad or Robert approves it",
    nextStage: "build_submitted",
    fields: [],
  },
  schedule_build: {
    buttonLabel: "Schedule build",
    subtitle: "Set the build date for Robert to schedule crew",
    nextStage: "build_scheduled",
    fields: [
      { key: "sq", label: "Total squares", dealColumn: "total_squares", type: "number", required: true, placeholder: "e.g. 32" },
      { key: "build_date", label: "Build date", dealColumn: "build_scheduled_date", type: "date", required: true },
    ],
  },
  build_scheduled: {
    buttonLabel: "Complete build",
    subtitle: "Mark the roof as complete — deal goes straight to commissions + supplements",
    nextStage: "ready_for_supps",
    nextPhase: "closing",
    fields: [
      { key: "done_date", label: "Build completed date", dealColumn: "build_completed_date", type: "date", required: true },
    ],
  },

  // ── CLOSING ──
  // May 7, 2026 — roof_complete stage retired. "Complete build" sends the deal
  // directly to ready_for_supps. The track_stage_change trigger stamps
  // commission_ready_date on entry (so the deal lands in Chad's commission
  // queue immediately), and TransitionButton fires notifyShannonOnSupplementRelease
  // (so Shannon picks it up in her Supplements queue immediately too). Chad's
  // supp-notes textarea on his Build Follow-ups tab still operates on the deal
  // post-transition. Commission lifecycle (commission_approved / commission_paid)
  // runs in PARALLEL to the supplement stages as flags on the deal — not as stages.
  // closed_won is reached after register_oc_warranty completes.

  ready_for_supps: {
    buttonLabel: "Sent supplements to Apex",
    subtitle: "Mark when you've sent the supplement package to Apex/insurance.",
    nextStage: "ready_for_updated_invoice",
    fields: [
      { key: "chad_notes_present", label: "Chad's supplement notes received", dealColumn: "chad_supplement_notes", type: "check", required: true, checkFn: "chad_supplement_notes_present" },
      { key: "sent_date", label: "Date sent to Apex", dealColumn: "supplement_submitted_to_insurance_date", type: "date", required: true },
    ],
  },

  ready_for_updated_invoice: {
    buttonLabel: "Upload supplement invoice",
    subtitle: "Apex returned with the rebuilt estimate. Upload it and capture the invoice amount.",
    nextStage: "supps_waiting_on_final_invoice",
    fields: [
      { key: "supp_invoice_doc", label: "Supplement invoice (Apex PDF)", dealColumn: "supplement_invoice_uploaded", type: "upload", required: true, docType: "supplement_invoice" },
      { key: "supp_invoice_amt", label: "Supplement amount requested", dealColumn: "supplement_invoice_amount", type: "currency", required: true, placeholder: "0.00", hint: "The ADDED amount requested from the carrier — not Apex's revised estimate total. Never shown to the homeowner until approved.", warnFn: "supplement_vs_rcv" },
    ],
  },

  supps_waiting_on_final_invoice: {
    buttonLabel: "Finalized insurance uploaded",
    subtitle: "Insurance approved. Upload finalized paperwork and capture approved supplement amount.",
    nextStage: "collecting",
    fields: [
      { key: "final_ins_doc", label: "Finalized insurance paperwork (PDF — the carrier's revised estimate)", dealColumn: "finalized_insurance_uploaded", type: "upload", required: true, docType: "finalized_insurance_paperwork" },
      { key: "supp_approved_amt", label: "Approved supplement amount", dealColumn: "supplement_approved_amount", type: "currency", required: true, placeholder: "0.00", hint: "The added amount the carrier approved — this is what lands on the homeowner's invoice." },
      { key: "approved_date", label: "Insurance approval date", dealColumn: "supplement_approved_date", type: "date", required: true },
    ],
  },

  // Deprecated alias for supps_waiting_on_final_invoice. Same UX so deals already
  // sitting at the old enum value flow correctly. Drop after 30-day window.
  supps_waiting_on_claims: {
    buttonLabel: "Finalized insurance uploaded",
    subtitle: "Insurance approved. Upload finalized paperwork and capture approved supplement amount.",
    nextStage: "collecting",
    fields: [
      { key: "final_ins_doc", label: "Finalized insurance paperwork (PDF — the carrier's revised estimate)", dealColumn: "finalized_insurance_uploaded", type: "upload", required: true, docType: "finalized_insurance_paperwork" },
      { key: "supp_approved_amt", label: "Approved supplement amount", dealColumn: "supplement_approved_amount", type: "currency", required: true, placeholder: "0.00", hint: "The added amount the carrier approved — this is what lands on the homeowner's invoice." },
      { key: "approved_date", label: "Insurance approval date", dealColumn: "supplement_approved_date", type: "date", required: true },
    ],
  },

  collecting: {
    buttonLabel: "All money collected",
    subtitle: "Confirm all funds (insurance + HO + supplement) are in the bank.",
    nextStage: "register_oc_warranty",
    fields: [
      { key: "balance_zero", label: "Balance remaining = $0", dealColumn: "post_build_balance_remaining", type: "check", required: true, checkFn: "balance_zero" },
    ],
  },

  // Same exit gate as collecting — auto-promoted from collecting at 30d unpaid by pg_cron (Ship 7).
  delinquent: {
    buttonLabel: "All money collected",
    subtitle: "Final invoice 30+ days unpaid. Confirm collection to advance.",
    nextStage: "register_oc_warranty",
    fields: [
      { key: "balance_zero", label: "Balance remaining = $0", dealColumn: "post_build_balance_remaining", type: "check", required: true, checkFn: "balance_zero" },
    ],
  },

  register_oc_warranty: {
    buttonLabel: "OC warranty registered",
    subtitle: "Owens Corning warranty registered with the manufacturer.",
    nextStage: "closed_won",
    // Registration date dropped per Greg (May 21, 2026) — not needed. The OC
    // warranty certificate is the only requirement to close the deal won.
    fields: [
      { key: "oc_cert", label: "OC warranty certificate (PDF)", dealColumn: "oc_warranty_certificate_uploaded", type: "upload", required: true, docType: "oc_warranty_certificate" },
    ],
  },
};

/**
 * Get the transition config for a deal's current stage.
 * Returns null if no transition is configured (terminal stages, etc.)
 */
export function getTransitionConfig(stage: DealStage | null): TransitionConfig | null {
  if (!stage) return null;
  return TRANSITION_CONFIGS[stage] ?? null;
}

/**
 * Get the button label for a stage's transition.
 * Falls back to generic "Advance" if no config exists.
 */
export function getTransitionButtonLabel(stage: DealStage | null): string {
  const config = getTransitionConfig(stage);
  return config?.buttonLabel ?? "Advance";
}
